/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: CdrSettingsPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the CDR Settings module.
----------------------------------------------------------------*/


import { useCallback, useState } from 'react';
import { Database, Code, UploadCloud } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { useNavigate } from 'react-router-dom';
import { ModuleFormHeader, ModuleFormLayout } from '../../components/layouts';
import { CdrGeneralSettingsBox } from './components/CdrGeneralSettingsBox';
import { CdrFieldsBox } from './components/CdrFieldsBox';
import { CdrXmlPreviewModal } from './components/CdrXmlPreviewModal';
import { useCdrSettingsData } from './hooks';
import { previewCdrXml, applyCdrSettings } from './api';

export default function CdrSettingsPage() {
    const { t } = useI18n();
    const { showError, showSuccess } = useNotifications();
    const navigate = useNavigate();

    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [xmlPreview, setXmlPreview] = useState('');

    const {
        settingsData, fields, isLoading, isSavingSettings, hasAnyChanges,
        handleSettingsChange, handleFieldChange, handleAddField, handleRemoveField,
        handleFieldDragEnd, saveAll, resetToOriginal,
    } = useCdrSettingsData({
        onSuccess: (msg) => showSuccess(msg),
        onError: (err) => showError(err.message),
    });

    // -- XML actions -------------------------------------------
    const handlePreviewXml = useCallback(async () => {
        setIsPreviewing(true);
        try {
            const result = await previewCdrXml();
            const xml = typeof result?.data?.xml === 'string' ? result.data.xml : '';
            if (xml) setXmlPreview(xml);
            showSuccess(t('cdrSettings.messages.previewSuccess'));
        } catch (error) {
            showError(error instanceof Error ? error.message : t('cdrSettings.messages.previewError'));
        } finally { setIsPreviewing(false); }
    }, [showError, showSuccess, t]);

    const handleApplyToNodes = useCallback(async () => {
        setIsApplying(true);
        try {
            const result = await applyCdrSettings();
            const xml = typeof result?.data?.xml === 'string' ? result.data.xml : '';
            if (xml) setXmlPreview(xml);
            showSuccess(t('cdrSettings.messages.applySuccess'));
        } catch (error) {
            showError(error instanceof Error ? error.message : t('cdrSettings.messages.applyError'));
        } finally { setIsApplying(false); }
    }, [showError, showSuccess, t]);

    const handleCopyXml = useCallback(async () => {
        if (!xmlPreview) return;
        try {
            await navigator.clipboard.writeText(xmlPreview);
            showSuccess(t('cdrSettings.messages.copySuccess'));
        } catch { showError(t('cdrSettings.messages.copyError')); }
    }, [showError, showSuccess, t, xmlPreview]);

    // -- Render ------------------------------------------------
    return (
        <div className="p-6 pb-12 space-y-4">
            <ModuleFormHeader
                icon={<Database className="h-5 w-5 text-primary" />}
                title={t('cdrSettings.title')}
                backButtonText={t('common.exit')}
                onBack={() => navigate(-1)}
                actions={[
                    { icon: <Code className="h-4 w-4" />, tooltip: t('cdrSettings.actions.previewXml'), onClick: () => void handlePreviewXml(), disabled: isPreviewing || isLoading || isSavingSettings },
                    { icon: <UploadCloud className="h-4 w-4" />, tooltip: t('cdrSettings.actions.applyToNodes'), onClick: () => void handleApplyToNodes(), disabled: isApplying || isLoading || isSavingSettings },
                ]}
            />

            <ModuleFormLayout
                onSubmit={(e) => { e.preventDefault(); void saveAll(); }}
                onCancel={resetToOriginal}
                isSaving={isSavingSettings}
                saveText={t('common.save')}
                cancelText={t('common.cancel')}
                isEditMode
                hasChanges={hasAnyChanges}
                wrapChildrenInCard={false}
                className="mt-4"
                spacingClass="space-y-4"
                isLoading={isLoading}
            >
                <CdrGeneralSettingsBox
                    settingsData={settingsData}
                    isLoading={isLoading}
                    onChange={handleSettingsChange}
                />
                <CdrFieldsBox
                    fields={fields}
                    isLoading={isLoading}
                    onFieldChange={handleFieldChange}
                    onRemoveField={handleRemoveField}
                    onAddField={handleAddField}
                    onDragEnd={handleFieldDragEnd}
                />
            </ModuleFormLayout>

            <CdrXmlPreviewModal
                xml={xmlPreview}
                onCopy={handleCopyXml}
                onClose={() => setXmlPreview('')}
            />
        </div>
    );
}
